/**
 * Form controls.
 *
 * Grouped in one module because they share one hard problem — wiring a label, a
 * hint and an error message to a control so that a screen reader announces all
 * three — and solving it once, in `Field`, is the only way it stays solved.
 * Every control here is designed to be used inside a `Field`, and each one
 * accepts the ids `Field` hands down through context rather than inventing its
 * own.
 *
 * The three controls built on a Radix primitive — checkbox, switch, select —
 * live in `Choice.tsx` instead, and `useField` is exported for them. Not a
 * taxonomy: this module is reachable from the sign-in screen, so everything
 * it imports ships to a visitor who has not signed in yet, and
 * `@radix-ui/react-select` was the largest single thing arriving that way for
 * a control used on one lazily-loaded route. The split does not by itself
 * move it — `Choice.tsx`'s own comment has the three measurements and the
 * rule they establish — but it is what makes moving it a one-line change.
 */

import {
  createContext,
  forwardRef,
  useContext,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
} from 'react';

import { cx } from '~/lib/cx';
import { Search } from './icons';

import styles from './Form.module.css';

/* ====================================================================== *
 * Field
 * ====================================================================== */

interface FieldContextValue {
  controlId: string;
  describedBy: string | undefined;
  invalid: boolean;
}

const FieldContext = createContext<FieldContextValue | null>(null);

export function useField(explicitId?: string): FieldContextValue {
  const fallbackId = useId();
  const context = useContext(FieldContext);
  return context ?? { controlId: explicitId ?? fallbackId, describedBy: undefined, invalid: false };
}

export interface FieldProps {
  label: ReactNode;
  /** Guidance shown before the user has done anything wrong. */
  hint?: ReactNode;
  /**
   * What went wrong. Presence of this is what makes the control invalid — there
   * is no separate `invalid` prop, so a field cannot be marked invalid with
   * nothing to read, which is the state that leaves a screen-reader user
   * knowing only that something is wrong.
   */
  error?: ReactNode;
  /** Marks the control required and shows the marker. */
  required?: boolean;
  /**
   * Hide the label visually but keep it for assistive technology.
   *
   * Used where the surrounding layout already names the control — a search box
   * under a heading that says Search. Never used to save space on a form.
   */
  labelHidden?: boolean;
  children: ReactNode;
  className?: string;
}

export function Field({
  label,
  hint,
  error,
  required = false,
  labelHidden = false,
  children,
  className,
}: FieldProps) {
  const base = useId();
  const controlId = `${base}-control`;
  const hintId = `${base}-hint`;
  const errorId = `${base}-error`;

  // Order matters: a screen reader reads `aria-describedby` in the order given,
  // and the error is the more urgent of the two.
  const describedBy = [error ? errorId : null, hint ? hintId : null].filter(Boolean).join(' ');

  return (
    <FieldContext.Provider
      value={{
        controlId,
        describedBy: describedBy || undefined,
        invalid: Boolean(error),
      }}
    >
      <div className={cx(styles.field, className)}>
        <label
          htmlFor={controlId}
          className={cx(styles.label, labelHidden && styles.visuallyHidden)}
        >
          {label}
          {required ? (
            <span className={styles.required} aria-hidden="true">
              *
            </span>
          ) : null}
        </label>

        {children}

        {hint ? (
          <p id={hintId} className={styles.hint}>
            {hint}
          </p>
        ) : null}

        {error ? (
          // `role="alert"` and not `aria-live="polite"`: a validation failure
          // the user caused by submitting is worth interrupting for, and a
          // polite region is routinely missed when focus moves at the same time.
          <p id={errorId} className={styles.error} role="alert">
            {error}
          </p>
        ) : null}
      </div>
    </FieldContext.Provider>
  );
}

/* ====================================================================== *
 * Text input
 * ====================================================================== */

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** A 16px glyph inside the control, before the text. Decorative. */
  iconStart?: ReactNode;
  /** Rendered after the text — a unit, a clear button, a count. */
  slotEnd?: ReactNode;
  invalid?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { iconStart, slotEnd, invalid, className, id, ...rest },
  ref,
) {
  const field = useField(id);
  const isInvalid = invalid ?? field.invalid;

  return (
    <div className={cx(styles.control, isInvalid && styles.controlInvalid, className)}>
      {iconStart ? <span className={styles.adornment}>{iconStart}</span> : null}
      <input
        ref={ref}
        id={id ?? field.controlId}
        className={styles.input}
        aria-invalid={isInvalid || undefined}
        aria-describedby={field.describedBy}
        {...rest}
      />
      {slotEnd ? <span className={styles.adornment}>{slotEnd}</span> : null}
    </div>
  );
});

export const SearchField = forwardRef<HTMLInputElement, Omit<InputProps, 'iconStart' | 'type'>>(
  function SearchField(props, ref) {
    return <Input ref={ref} type="search" iconStart={<Search />} {...props} />;
  },
);

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { invalid, className, id, rows = 4, ...rest },
  ref,
) {
  const field = useField(id);
  const isInvalid = invalid ?? field.invalid;

  return (
    <textarea
      ref={ref}
      id={id ?? field.controlId}
      rows={rows}
      className={cx(styles.textarea, isInvalid && styles.controlInvalid, className)}
      aria-invalid={isInvalid || undefined}
      aria-describedby={field.describedBy}
      {...rest}
    />
  );
});
